/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CheckCircle, XCircle, Clock, AlertTriangle,
  ArrowLeft, RefreshCw, FileText, Home,
  CreditCard
} from 'lucide-react';
import { usePayment, usePollPaymentStatus } from '@/hooks/api/usePayments';
import { PaymentStatus } from '@/types/superadmin.types';

type ResultStatus = 'success' | 'failed' | 'pending' | 'cancelled' | 'loading' | 'error';

interface StatusConfig {
  icon: typeof CheckCircle;
  title: string;
  description: string;
  bgColor: string;
  iconColor: string;
  showPoll: boolean;
}

const statusConfigs: Record<ResultStatus, StatusConfig> = {
  success: {
    icon: CheckCircle,
    title: 'payment.result.successTitle',
    description: 'payment.result.successDescription',
    bgColor: 'bg-emerald-500/15',
    iconColor: 'text-emerald-500',
    showPoll: false,
  },
  failed: {
    icon: XCircle,
    title: 'payment.result.failedTitle',
    description: 'payment.result.failedDescription',
    bgColor: 'bg-red-500/15',
    iconColor: 'text-red-500',
    showPoll: false,
  },
  pending: {
    icon: Clock,
    title: 'payment.result.pendingTitle',
    description: 'payment.result.pendingDescription',
    bgColor: 'bg-amber-500/15',
    iconColor: 'text-amber-500',
    showPoll: true,
  },
  cancelled: {
    icon: XCircle,
    title: 'payment.result.cancelledTitle',
    description: 'payment.result.cancelledDescription',
    bgColor: 'bg-gray-500/15',
    iconColor: 'text-gray-400',
    showPoll: false,
  },
  loading: {
    icon: RefreshCw,
    title: 'payment.result.loadingTitle',
    description: 'payment.result.loadingDescription',
    bgColor: 'bg-blue-500/15',
    iconColor: 'text-blue-500',
    showPoll: false,
  },
  error: {
    icon: AlertTriangle,
    title: 'payment.result.errorTitle',
    description: 'payment.result.errorDescription',
    bgColor: 'bg-red-500/15',
    iconColor: 'text-red-500',
    showPoll: false,
  },
};

// Map payment status to result status
function mapPaymentStatus(status: PaymentStatus | undefined): ResultStatus {
  if (!status) return 'loading';
  switch (status) {
    case 'PAID':
      return 'success';
    case 'FAILED':
      return 'failed';
    case 'CANCELLED':
      return 'cancelled';
    case 'PENDING':
    case 'PROCESSING':
      return 'pending';
    case 'REFUNDED':
    case 'PARTIALLY_REFUNDED':
      return 'success'; // Treat as completed
    default:
      return 'pending';
  }
}

export default function PaymentResultPage() {
  const { t } = useTranslation('core');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const paymentId = searchParams.get('payment');
  const transactionId = searchParams.get('transaction');
  const urlStatus = searchParams.get('status') as 'success' | 'failed' | 'cancelled' | null;

  const [resultStatus, setResultStatus] = useState<ResultStatus>('loading');
  const [pollCount, setPollCount] = useState(0);

  // Fetch payment details if we have a payment ID
  const { data: payment, isLoading, error, refetch } = usePayment(paymentId || '');

  // Poll mutation for checking status
  const pollMutation = usePollPaymentStatus();

  // Determine initial status from URL or payment data
  useEffect(() => {
    if (urlStatus === 'cancelled') {
      setResultStatus('cancelled');
      return;
    }

    if (isLoading) {
      setResultStatus('loading');
      return;
    }

    if (error && !payment) {
      setResultStatus('error');
      return;
    }

    if (payment) {
      setResultStatus(mapPaymentStatus(payment.status));
    }
  }, [payment, isLoading, error, urlStatus]);

  // Auto-poll for pending payments
  useEffect(() => {
    if (resultStatus !== 'pending' || pollCount >= 10) return;

    const pollInterval = setInterval(async () => {
      try {
        if (payment?.externalReference) {
          await pollMutation.mutateAsync(payment.externalReference);
        }
        await refetch();
        setPollCount(prev => prev + 1);
      } catch {
        // Silently continue polling
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
  }, [resultStatus, pollCount, payment?.externalReference, pollMutation, refetch]);

  const config = statusConfigs[resultStatus];
  const Icon = config.icon;

  const handleRetry = () => {
    // Navigate back to organization subscription page or dashboard
    if (payment?.organizationId) {
      navigate(`/superadmin/organizations/${payment.organizationId}/subscription`);
    } else {
      navigate('/superadmin');
    }
  };

  const handleViewInvoice = () => {
    if (payment?.invoiceNumber) {
      navigate(`/superadmin/invoices?search=${payment.invoiceNumber}`);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Result card */}
        <div className="bg-surface border border-border-subtle rounded-2xl p-8 text-center">
          {/* Icon */}
          <div className={`w-20 h-20 mx-auto rounded-full ${config.bgColor} flex items-center justify-center mb-6`}>
            <Icon className={`w-10 h-10 ${config.iconColor} ${resultStatus === 'loading' ? 'animate-spin' : ''}`} />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {t(config.title)}
          </h1>

          {/* Description */}
          <p className="text-muted mb-6">
            {t(config.description)}
          </p>

          {/* Payment details */}
          {payment && (
            <div className="bg-background rounded-lg p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted">{t('payment.result.amount')}</span>
                  <span className="font-mono font-semibold text-foreground">
                    {payment.currency === 'USD' ? '$' : ''}
                    {payment.amount.toLocaleString()}
                    {payment.currency !== 'USD' ? ` ${payment.currency}` : ''}
                  </span>
                </div>
                {payment.invoiceNumber && (
                  <div className="flex justify-between">
                    <span className="text-muted">{t('payment.result.invoice')}</span>
                    <span className="font-mono text-foreground">{payment.invoiceNumber}</span>
                  </div>
                )}
                {payment.reference && (
                  <div className="flex justify-between">
                    <span className="text-muted">{t('payment.result.reference')}</span>
                    <span className="font-mono text-xs text-foreground truncate max-w-[150px]">
                      {payment.reference}
                    </span>
                  </div>
                )}
                {payment.paidAt && (
                  <div className="flex justify-between">
                    <span className="text-muted">{t('payment.result.paidAt')}</span>
                    <span className="text-foreground">
                      {new Date(payment.paidAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Polling status */}
          {config.showPoll && pollCount < 10 && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted mb-6">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {t('payment.result.polling')}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {resultStatus === 'success' && (
              <>
                {payment?.invoiceNumber && (
                  <button
                    onClick={handleViewInvoice}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
                  >
                    <FileText className="w-4 h-4" /> {t('payment.result.viewInvoice')}
                  </button>
                )}
                <Link
                  to="/"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
                >
                  <Home className="w-4 h-4" /> {t('payment.result.goHome')}
                </Link>
              </>
            )}

            {resultStatus === 'failed' && (
              <>
                <button
                  onClick={handleRetry}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
                >
                  <CreditCard className="w-4 h-4" /> {t('payment.result.retry')}
                </button>
                <Link
                  to="/"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
                >
                  <Home className="w-4 h-4" /> {t('payment.result.goHome')}
                </Link>
              </>
            )}

            {resultStatus === 'cancelled' && (
              <>
                <button
                  onClick={handleRetry}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
                >
                  <CreditCard className="w-4 h-4" /> {t('payment.result.tryAgain')}
                </button>
                <Link
                  to="/"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
                >
                  <Home className="w-4 h-4" /> {t('payment.result.goHome')}
                </Link>
              </>
            )}

            {resultStatus === 'pending' && (
              <Link
                to="/"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
              >
                <Home className="w-4 h-4" /> {t('payment.result.goHome')}
              </Link>
            )}

            {resultStatus === 'error' && (
              <>
                <button
                  onClick={() => refetch()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
                >
                  <RefreshCw className="w-4 h-4" /> {t('common.retry')}
                </button>
                <Link
                  to="/"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-border-subtle rounded-lg font-medium hover:bg-hover"
                >
                  <Home className="w-4 h-4" /> {t('payment.result.goHome')}
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Back link */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-muted hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
        </div>
      </div>
    </div>
  );
}
